import React, { useState, useEffect } from 'react';
import { HiSparkles } from "react-icons/hi2";
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import { useLocation } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';

const SignupLink = () => {
  return (
    <NamedLink
      id="signup-link"
      name="SignupForUserTypePage"
      params={{ userType: 'customer' }}
      className={css.topbarLink}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink id="login-link" name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const DownloadLink = () => {
  const [mounted, setMounted] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({
    isIOS: false,
    isAndroid: false,
  });

  useEffect(() => {
    setMounted(true);

    if (typeof window !== 'undefined') {
      const ua = navigator.userAgent || '';

      setDeviceInfo({
        isIOS: /iphone|ipad|ipod|mac/i.test(ua),
        isAndroid: /android|windows/i.test(ua),
      });
    }
  }, []);

  if (!mounted) return null;

  const IOS_STORE_URL = process.env.REACT_APP_IOS_APP_STORE_URL;
  const ANDROID_STORE_URL = process.env.REACT_APP_ANDROID_PLAY_STORE_URL;

  if (!IOS_STORE_URL && !ANDROID_STORE_URL) return null;

  const storeUrl = deviceInfo.isIOS
    ? IOS_STORE_URL || ANDROID_STORE_URL
    : deviceInfo.isAndroid
      ? ANDROID_STORE_URL || IOS_STORE_URL
      : IOS_STORE_URL || ANDROID_STORE_URL;

  return (
    <a
      id="download-link"
      href={storeUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={css.downloadLink}
    >
      <span className={css.newLabel}>
        <HiSparkles aria-hidden="true" />
        NEW
      </span>

      <img className={css.downloadButton} src="/static/mainassets/google-play.png" alt="" aria-hidden="true" />
      <span className={css.ctaText}>Download The Purfurry App!</span>
    </a>
  );
};

const InboxLink = ({ notificationCount, currentUserHasListings, currentUser, inboxTab }) => {
  const notificationDotMaybe =
    notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const notificationDot =
    currentUser && !!currentUser.id && !currentUser.attributes.emailVerified ? (
      <div className={css.notificationDot} />
    ) : (
      notificationDotMaybe
    );
  const isCustomer = currentUser?.attributes?.profile?.publicData?.userType === 'customer';
  const isProvider = currentUser?.attributes?.profile?.publicData?.userType === 'provider';

  return (
    <NamedLink
      id="inbox-link"
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: isProvider ? 'sales' : 'orders' }}
      to={{
        pathname: `/inbox/${isProvider ? 'sales' : 'orders'}`,
        search: '?filter=all',
      }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const AboutusLink = () => {
  return (
    <NamedLink name="AboutPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.aboutus" />
      </span>
    </NamedLink>
  );
};

const BlogsLink = () => {
  return (
    <NamedLink name="CMSPage" params={{ pageId: 'blogs' }} className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.blogs" />
      </span>
    </NamedLink>
  );
};

const ToolsLink = () => {
  return (
    <NamedLink name="CMSPage" params={{ pageId: 'purfurry_tools' }} className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>Tools</span>
    </NamedLink>
  );
};

const HomeLink = ({ currentUser }) => {
  const location = useLocation();
  const isProvider = currentUser?.attributes?.profile?.publicData?.userType === 'provider';

  const isHomePage = location.pathname === '/' || location.pathname.startsWith('/listings');

  if (isHomePage) {
    return null;
  }
  if (isProvider) {
    return (
      <NamedLink name="ManageListingsPage" className={css.topbarLink}>
        <span className={css.topbarLinkLabel}>
          <FormattedMessage id="TopbarDesktop.home" />
        </span>
      </NamedLink>
    );
  }
  return (
    <NamedLink name="LandingPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.home" />
      </span>
    </NamedLink>
  );
};

const RebookLink = () => {
  return (
    <NamedLink
      name="InboxPage"
      params={{ tab: 'rebook' }}
      className={classNames(css.topbarLink, css.rebookLink)}
    >
      <span className={classNames(css.topbarLinkLabel, css.rebookLinkTopbar)}>Re-book</span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink, intl }) => {
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <Menu skipFocusOnNavigation={true}>
      <MenuLabel
        id="profile-menu-label"
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({
          id: 'TopbarDesktop.screenreader.profileMenu',
        })}
      >
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {[
          userType === 'provider' && (
            <MenuItem key="NewListingPage">
              <NamedLink
                className={classNames(css.menuLink, currentPageClass('NewListingPage'))}
                name="NewListingPage"
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.newListingLink" />
              </NamedLink>
            </MenuItem>
          ),
        ].filter(Boolean)}
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton
            rootClassName={css.logoutButton}
            className={css.menuLink}
            onClick={onLogout}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    isProvider,
    showCreateListingsLink,
    inboxTab,
    hasRebookable,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
      currentUser={currentUser}
      inboxTab={inboxTab}
    />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      intl={intl}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;
  const aboutusLinkMaybe = <AboutusLink />;
  const blogsLinkMaybe = <BlogsLink />;
  const toolsLinkMaybe = <ToolsLink />;
  const homeLinkMaybe = <HomeLink currentUser={currentUser} />;
  const rebookLinkMaybe = authenticatedOnClientSide && hasRebookable ? <RebookLink /> : null;
  const downloadLinkMaybe = <DownloadLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  return (
    <nav className={classes}>
      <LinkedLogo
        id="logo-topbar-desktop"
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
        isProvider={currentUser?.attributes?.profile?.publicData?.userType === 'provider'}
        aria-label={intl.formatMessage({
          id: 'TopbarDesktop.screenreader.topbarNavigation',
        })}
      />
      {/* {searchFormMaybe} */}
      <div className={css.navLinks}>
        {!isAuthenticated && (
          <CustomLinksMenu
            currentPage={currentPage}
            customLinks={customLinks}
            intl={intl}
            hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
            showCreateListingsLink={showCreateListingsLink}
          />
        )}
        {downloadLinkMaybe}
        {rebookLinkMaybe}
        {homeLinkMaybe}
        {aboutusLinkMaybe}
        {blogsLinkMaybe}
        {toolsLinkMaybe}
        {inboxLinkMaybe}
        {profileMenuMaybe}
        {signupLinkMaybe}
        {loginLinkMaybe}
      </div>
    </nav>
  );
};

export default TopbarDesktop;
